import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { config } from '../constants/config';
import { acceptRefreshedToken } from '../apiTools/apiClient';

/**
 * Helper function to check if a user with a given primary role can switch to a target role
 * @param {string} primaryRole - The user's primary role
 * @param {string} targetRole - The role the user wants to switch to
 * @returns {boolean} Whether the switch is allowed
 */
function canSwitchToRole(primaryRole, targetRole) {
  if (primaryRole === 'admin') {
    return true; // Admin can access all roles
  } else if (primaryRole === 'bookCreator') {
    return targetRole === 'bookCreator' || targetRole === 'voiceActor';
  } else if (primaryRole === 'voiceActor') {
    return targetRole === 'voiceActor';
  }
  return false;
}

// Thunks for fetching user profiles
export const fetchUserProfiles = createAsyncThunk(
  'userRole/fetchProfiles',
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.BASE_URL}/users/${userId}/profiles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch user profiles');
    }
  }
);

// Thunk for creating an additional profile
export const createAdditionalProfile = createAsyncThunk(
  'userRole/createAdditionalProfile',
  async ({ userId, profileType, profileData = {} }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');

      // Check if profileData contains a profileImage that needs to be uploaded
      let updatedProfileData = { ...profileData };

      if (profileData.profileImage && typeof profileData.profileImage === 'object' && profileData.profileImage.file) {
        // We need to upload the image file
        const file = profileData.profileImage.file;
        const formData = new FormData();
        formData.append('profileImage', file); // Use 'profileImage' as the field name to match the server's expectation

        // Upload the file using the profile image upload endpoint
        const uploadResponse = await axios.post(
          `${config.BASE_URL}/upload-profile-image/${userId}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // Replace the file object with the URL from the response
        if (uploadResponse.data) {
          // Check different possible response formats
          if (uploadResponse.data.fileUrl) {
            updatedProfileData.profileImage = uploadResponse.data.fileUrl;
          } else if (uploadResponse.data.url) {
            updatedProfileData.profileImage = uploadResponse.data.url;
          } else if (typeof uploadResponse.data === 'string' && uploadResponse.data.includes('http')) {
            // If the response is a string URL
            updatedProfileData.profileImage = uploadResponse.data;
          } else {
            console.error('Upload response did not contain expected URL:', uploadResponse.data);
            // Use an empty string if the upload fails
            updatedProfileData.profileImage = '';
          }
        } else {
          console.error('Upload response did not contain data:', uploadResponse);
          updatedProfileData.profileImage = '';
        }
      }

      // Create the profile with the processed data
      const response = await axios.post(
        `${config.BASE_URL}/users/${userId}/profiles`,
        {
          profileType,
          profileData: updatedProfileData  // Send as nested object, not spread
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // If a new token was returned, update it in localStorage.
      //
      // ⭐ GUARDED. See acceptRefreshedToken in apiTools/apiClient.js: a token handed back by a
      // response is not automatically fresher than the one we hold, and writing a dead one over a
      // live one ends the session a minute after it started.
      if (response.data.token && acceptRefreshedToken(response.data.token).ok) {
        localStorage.setItem('authToken', response.data.token);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create additional profile');
    }
  }
);

// Thunk for updating a user profile
export const updateUserProfile = createAsyncThunk(
  'userRole/updateUserProfile',
  async ({ userId, profileUuid, profileType, profileData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');

      // NOTE: Image upload is now handled by the useMediaUpload hook before form submission
      // The profileData should already contain the profilePictureUrl as a string URL
      // No need to handle file uploads here anymore

      console.log('🚀 updateUserProfile - Thunk received:', {
        userId,
        profileUuid,
        profileType,
        profileDataKeys: Object.keys(profileData),
        profileData
      });

      console.log('📦 updateUserProfile - Profile data details:');
      Object.entries(profileData).forEach(([key, value]) => {
        console.log(`  ${key}:`, typeof value === 'object' ? JSON.stringify(value) : value);
      });

      // Use profileType in the URL, not UUID - this matches the old working code
      const apiUrl = `${config.BASE_URL}/users/${userId}/profiles/${profileType}`;
      console.log('🌐 updateUserProfile - API endpoint:', apiUrl);
      console.log('📮 updateUserProfile - Request body being sent:', JSON.stringify(profileData, null, 2));

      // Update the profile with the data
      const response = await axios.put(
        apiUrl,
        profileData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ updateUserProfile - Server response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ updateUserProfile - Error:', error.response?.data || error);
      return rejectWithValue(error.response?.data || 'Failed to update profile');
    }
  }
);

// Thunk for updating primary role
export const updatePrimaryRole = createAsyncThunk(
  'userRole/updatePrimaryRole',
  async ({ userId, primaryRole }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(`${config.BASE_URL}/users/${userId}/primary-role`,
        { primaryRole },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Update token in localStorage — guarded, as above.
      if (response.data.token && acceptRefreshedToken(response.data.token).ok) {
        localStorage.setItem('authToken', response.data.token);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update primary role');
    }
  }
);

// Initial state
const initialState = {
  profiles: [],
  activeRole: null,
  persistSelections: true,
  status: 'idle',
  error: null
};

// Slice
const userRoleSlice = createSlice({
  name: 'userRole',
  initialState,
  reducers: {
    switchActiveRole(state, action) {
      state.activeRole = action.payload;
    },
    setPersistSelections(state, action) {
      state.persistSelections = action.payload;
    },
    clearRoleError(state) {
      state.error = null;
    },
    resetRoleState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Fetch user profiles
      .addCase(fetchUserProfiles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserProfiles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.profiles = action.payload;
        // Set default active role if not already set
        if (!state.activeRole && action.payload.length > 0) {
          state.activeRole = action.payload[0].type;
        }
      })
      .addCase(fetchUserProfiles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Create additional profile
      .addCase(createAdditionalProfile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createAdditionalProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.profiles.push(action.payload);
      })
      .addCase(createAdditionalProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Update user profile
      .addCase(updateUserProfile.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Find and update the profile in the profiles array
        const index = state.profiles.findIndex(profile =>
          profile.type === action.payload.type && profile.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.profiles[index] = action.payload;
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Update primary role
      .addCase(updatePrimaryRole.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updatePrimaryRole.fulfilled, (state, action) => {
        state.status = 'succeeded';

        // Update isPrimary flags on all profiles
        state.profiles.forEach(profile => {
          profile.isPrimary = profile.type === action.payload.primaryRole;
        });

        // If the active role is not accessible with the new primary role, switch to the primary role
        const canAccessActiveRole = canSwitchToRole(action.payload.primaryRole, state.activeRole);
        if (!canAccessActiveRole) {
          state.activeRole = action.payload.primaryRole;
        }
      })
      .addCase(updatePrimaryRole.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      });
  }
});

// Export actions
export const {
  switchActiveRole,
  setPersistSelections,
  clearRoleError,
  resetRoleState
} = userRoleSlice.actions;

// Export selectors
export const selectUserProfiles = (state) => state.user.userRole.profiles;
export const selectActiveRole = (state) => state.user.userRole.activeRole;
export const selectPersistSelections = (state) => state.user.userRole.persistSelections;
export const selectUserRoleStatus = (state) => state.user.userRole.status;
export const selectUserRoleError = (state) => state.user.userRole.error;

// Export reducer
export default userRoleSlice.reducer;
